import { DEV_PUBLIC_ORIGIN, ROBOT_MEDIA_BASE_URL } from '/@/constants'

export function isJsonFileName (name: unknown): boolean {
  const s = String(name ?? '').trim().toLowerCase()
  return s.endsWith('.json')
}

export function getFullMediaUrl (path: string): string {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  const base = ROBOT_MEDIA_BASE_URL.replace(/\/$/, '')
  const normalized = path.replace(/^\//, '')
  if (normalized.startsWith(base.replace(/^\//, ''))) {
    return `${base}/${normalized.slice(base.replace(/^\//, '').length).replace(/^\//, '')}`
  }
  return `${base}/${normalized}`
}

function resolveAbsoluteUrlOrigin (): string {
  if (import.meta.env.VITE_APP_ENVIRONMENT === 'DEV') {
    return DEV_PUBLIC_ORIGIN
  }
  return window.location.origin
}

/** 将媒体路径转为带 origin 的绝对 URL（供后端/模型服务访问） */
export function toAbsoluteMediaUrl (url: string): string {
  const value = String(url || '').trim()
  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value
  const path = value.startsWith('/') ? value : getFullMediaUrl(value)
  return new URL(path, resolveAbsoluteUrlOrigin()).href
}

/** 将接口文件项转为 MediaPanel / 标注侧栏通用结构 */
export function normalizeMediaFile (el: Record<string, any>): Record<string, any> {
  const isFolder =
    !el.name && (!el.filePath || String(el.filePath).endsWith('/'))
  const fullUrl = el.filePath
    ? getFullMediaUrl(el.filePath)
    : el.object_key || ''

  el.object_keys = isFolder ? '' : fullUrl
  el.object_key = el.object_key || fullUrl
  el.file_name = el.file_name || el.name || '--'
  el.file_path =
    el.file_path ??
    (isFolder
      ? '--'
      : el.filePath
        ? fullUrl
        : el.object_key
          ? el.object_key
          : '--')
  el.create_time =
    el.create_time ||
    (el.time
      ? new Date(el.time)
          .toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          })
          .replace(/\//g, '-')
      : '--')

  return el
}

export interface TrainingVideoOption {
  path: string
  fileName: string
}

export function isMp4File (record: Record<string, any>): boolean {
  const name = String(record.file_name || record.name || '').toLowerCase()
  const path = String(
    record.filePath || record.file_path || record.object_key || record.object_keys || ''
  ).toLowerCase()
  return /\.mp4$/i.test(name) || /\.mp4(?:\?|$)/i.test(path)
}

export function isDetectedMediaPath (path: string): boolean {
  const normalized = String(path || '').replace(/\\/g, '/').toLowerCase()
  return normalized.includes('_detected')
}

export function canUseAsTrainingVideo (record: Record<string, any>): boolean {
  if (!isMp4File(record)) return false
  const fullUrl = getAnnotationImageUrl(record)
  // 只要有URL且不是detected路径就可以用作训练视频
  return !!fullUrl && !isDetectedMediaPath(fullUrl)
}

export function toTrainingVideoOption (
  record: Record<string, any>,
): TrainingVideoOption | null {
  if (!canUseAsTrainingVideo(record)) return null
  // 使用完整URL而不是相对路径
  const fullUrl = getAnnotationImageUrl(record)
  if (!fullUrl) return null
  return {
    path: toAbsoluteMediaUrl(fullUrl),
    fileName: String(record.file_name || record.name || fullUrl),
  }
}

export function isImageFile (record: Record<string, any>): boolean {
  const name = String(record.file_name || record.name || '').toLowerCase()
  const path = String(
    record.object_key || record.object_keys || record.file_path || ''
  ).toLowerCase()
  return (
    /\.(png|jpe?g|webp|bmp|gif)$/i.test(name) ||
    /\.(png|jpe?g|webp|bmp|gif)(\?|$)/i.test(path)
  )
}

export function getAnnotationFlag (record: Record<string, any>): number {
  const raw =
    record.annotationFlag ?? record.annotation_flag ?? record.annotationflag
  return Number(raw)
}

export function canEnterAnnotation (record: Record<string, any>): boolean {
  // 开发环境下不检查 annotationFlag 限制
  if (import.meta.env.VITE_APP_ENVIRONMENT === 'DEV') {
    return isImageFile(record)
  }
  return isImageFile(record) && getAnnotationFlag(record) === 1
}

/** 机器狗媒体：统一检查 annotationFlag 是否为 1 */
export function canEnterRobotDogAnnotation (record: Record<string, any>): boolean {
  // 开发环境下不检查 annotationFlag 限制
  if (import.meta.env.VITE_APP_ENVIRONMENT === 'DEV') {
    return isImageFile(record)
  }
  return isImageFile(record) && getAnnotationFlag(record) === 1
}

export function getAnnotationImageUrl (record: Record<string, any>): string {
  return String(record.object_keys || record.object_key || record.file_path || '')
}

/** 从完整 URL 或已是相对路径的字符串提取标注接口 path（不含域名/bucket 前缀） */
export function getAnnotationQueryPathFromUrl (url: string): string {
  const trimmed = String(url || '').trim()
  if (!trimmed) return ''
  if (!trimmed.startsWith('http')) {
    return trimmed.replace(/^\//, '').split('?')[0]
  }
  const base = ROBOT_MEDIA_BASE_URL.replace(/\/$/, '')
  if (trimmed.startsWith(base)) {
    return trimmed.slice(base.length).replace(/^\//, '').split('?')[0]
  }
  const bucketMatch = trimmed.match(/\/dj-cloud-bucket\/(.+?)(?:\?|$)/)
  if (bucketMatch?.[1]) return bucketMatch[1]
  return trimmed.split('?')[0]
}

/** 标注查询/保存使用的图片路径（优先相对路径，与接口 path 字段一致） */
export function getAnnotationQueryPath (record: Record<string, any>): string {
  const relative = String(record.filePath || record.objectKey || '').trim()
  if (relative && !relative.startsWith('http')) {
    return relative.replace(/^\//, '').split('?')[0]
  }
  return getAnnotationQueryPathFromUrl(getAnnotationImageUrl(record))
}

/** 标注页路由 id / 侧栏选中 id，与接口 path 一致（相对路径，不含域名前缀） */
export function getAnnotationImageId (record: Record<string, any>): string {
  return getAnnotationQueryPath(record)
}
